import { FC } from 'react';

import { AnimatePresence, motion } from 'motion/react';
import { useLocation } from 'react-router';

import { useAvatarStore } from '@/features/edit-profile-photo';

import { UserAvatar, usePersonalStore } from '@/entities/user';

import { CloseIcon } from '@/shared/assets';
import { ROUTES, cn, useUIStore, useViewportStore } from '@/shared/lib';
import { BackLink, CustomizeCloseButton, Navbar } from '@/shared/ui';
import { DashboardCustomizationBtn } from '@/shared/ui';

import {
  BACK_BUTTON_LINK,
  HIDDEN_AVATAR_PATHS,
  HIDDEN_NAV_PATHS,
  SHOWN_CUSTOMIZATION_BUTTON_PATHS,
  TRANSPARENT_PATHS,
} from '../../config';
import { useSplashScreenStore } from '../../model';
import Logo from './Logo';

const HeaderLeftSide = () => {
  const { pathname } = useLocation();

  const isBackLinkShown = !!BACK_BUTTON_LINK[pathname];

  return (
    <div className="w-[164px]">
      {pathname === ROUTES.CUSTOMIZE ? (
        <CustomizeCloseButton isLogo />
      ) : isBackLinkShown ? (
        <BackLink to={BACK_BUTTON_LINK[pathname]} />
      ) : (
        <Logo />
      )}
    </div>
  );
};

const HeaderRightSide: FC = () => {
  const { pathname } = useLocation();
  const isDesktop = useViewportStore((state) => state.isDesktop);
  const { userAvatar } = useAvatarStore();
  const isCustomizationBtnShown =
    SHOWN_CUSTOMIZATION_BUTTON_PATHS.includes(pathname);
  const isCustomizeBackShown = useUIStore(
    (state) => state.showCustomizeBackButton,
  );

  const isAvatarShown = !HIDDEN_AVATAR_PATHS.includes(pathname);
  const isShowSplashScreen = useSplashScreenStore(
    (state) => state.isShowSplashScreen,
  );
  return (
    <div
      id="header-right"
      className="flex w-[164px] items-center justify-end gap-8"
    >
      {isCustomizationBtnShown && !isShowSplashScreen && (
        <motion.div
          initial={isDesktop ? { opacity: 0 } : false}
          animate={isDesktop ? { opacity: 1 } : false}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <DashboardCustomizationBtn />
        </motion.div>
      )}
      {isAvatarShown ? (
        <UserAvatar avatar={userAvatar} />
      ) : isCustomizeBackShown && pathname === ROUTES.CUSTOMIZE ? (
        <CustomizeCloseButton />
      ) : (
        <BackLink icon={<CloseIcon className="!h-6 !w-6" />} />
      )}
    </div>
  );
};

const DashboardHeader: FC = () => {
  const isShowSplashScreen = useSplashScreenStore(
    (state) => state.isShowSplashScreen,
  );
  const { pathname } = useLocation();
  const isDesktop = useViewportStore((state) => state.isDesktop);
  const isTransparent = TRANSPARENT_PATHS.includes(pathname);
  const isNavShown = !HIDDEN_NAV_PATHS.includes(pathname);
  const isAcc = pathname.includes('account');
  const company = usePersonalStore((state) => state.company);

  return (
    <header
      className={cn('fixed top-0 z-50 w-full px-6 py-2 sm:px-10 lg:px-[80px]', {
        'bg-surface-00-transparent': isTransparent,
        'z-40': isAcc,
      })}
    >
      <div className="relative mx-auto flex min-h-16 max-w-spacing-header items-center justify-between">
        <HeaderLeftSide />
        <AnimatePresence mode="sync">
          {isDesktop && isShowSplashScreen ? (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                duration: 0.5,
                delay: 1.5,
                ease: 'easeOut',
              }}
              className="absolute left-1/2 top-[5px] -translate-x-1/2 whitespace-nowrap heading-l"
            >{`welcome, ${company?.name || ''}`}</motion.p>
          ) : (
            isNavShown && (
              <motion.div
                initial={isDesktop ? { opacity: 0 } : false}
                animate={isDesktop ? { opacity: 1 } : false}
                transition={{ duration: 0.5, ease: 'easeOut' }}
              >
                <Navbar />
              </motion.div>
            )
          )}
        </AnimatePresence>

        <HeaderRightSide />
      </div>
    </header>
  );
};

export default DashboardHeader;
